import { useState } from 'react'
import { Star, Trash2, MessageSquare } from 'lucide-react'
import { motion } from 'framer-motion'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import { useReviewSummary, useReviews, useCreateReview, useDeleteReview } from '@/features/review/api'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'

interface Props {
  facilityId: number
}

export default function FacilityReviewTab({ facilityId }: Props) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { data: summary } = useReviewSummary(facilityId)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useReviews(facilityId)
  const reviews = data?.pages.flatMap((p) => p.data.content) ?? []

  return (
    <motion.div
      className="flex flex-col gap-4"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* 요약 */}
      {summary && (
        <Card className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={22}
                className={n <= Math.round(summary.averageRating) ? 'fill-amber-400 text-amber-400' : 'text-cb-gray-200'}
              />
            ))}
          </div>
          <p className="text-h2 font-bold text-cb-gray-900">{summary.averageRating.toFixed(1)}</p>
          <p className="text-caption text-cb-gray-500">{summary.totalCount}개 후기</p>
        </Card>
      )}

      {/* 후기 작성 */}
      {isAuthenticated ? (
        <ReviewForm facilityId={facilityId} />
      ) : (
        <div className="rounded-xl bg-cb-gray-50 px-4 py-3 text-center text-body-sm text-cb-gray-500">
          후기를 작성하려면 <a href="/login" className="text-cb-primary font-semibold">로그인</a>이 필요합니다
        </div>
      )}

      {/* 후기 목록 */}
      {isLoading && <div className="flex justify-center py-8"><Spinner size="md" /></div>}

      {reviews.length === 0 && !isLoading && (
        <Card className="text-center py-8">
          <p className="text-body-sm text-cb-gray-500">아직 후기가 없어요</p>
          <p className="text-caption text-cb-gray-400 mt-1">첫 번째 후기를 남겨보세요</p>
        </Card>
      )}

      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} facilityId={facilityId} />
      ))}

      {hasNextPage && (
        <Button variant="ghost" size="md" fullWidth onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
          더보기
        </Button>
      )}
    </motion.div>
  )
}

/** 후기 작성 폼 */
function ReviewForm({ facilityId }: { facilityId: number }) {
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [content, setContent] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const createReview = useCreateReview()

  const handleSubmit = () => {
    if (rating === 0 || content.trim().length < 10) return
    createReview.mutate(
      { facilityId, rating, content: content.trim() },
      {
        onSuccess: () => {
          setRating(0)
          setContent('')
          setIsOpen(false)
        },
      }
    )
  }

  if (!isOpen) {
    return (
      <Button variant="secondary" size="lg" fullWidth onClick={() => setIsOpen(true)}>
        후기 작성하기
      </Button>
    )
  }

  return (
    <Card>
      <h4 className="text-body font-bold text-cb-gray-900 mb-3">후기 작성</h4>

      {/* 별점 선택 */}
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHoverRating(n)}
            onMouseLeave={() => setHoverRating(0)}
            onClick={() => setRating(n)}
            className="p-0.5"
          >
            <Star
              size={28}
              className={cn(
                'transition-colors',
                n <= (hoverRating || rating)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-cb-gray-200'
              )}
            />
          </button>
        ))}
        {rating > 0 && <span className="ml-2 text-body-sm font-bold text-cb-gray-700">{rating}점</span>}
      </div>

      {/* 내용 */}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="시설 이용 경험을 10자 이상 작성해주세요"
        className="w-full rounded-xl border border-cb-gray-200 p-3 text-body-sm text-cb-gray-900 placeholder:text-cb-gray-400 focus:outline-none focus:border-cb-primary resize-none"
        rows={4}
        maxLength={1000}
      />
      <p className="text-caption text-cb-gray-400 text-right mt-1">{content.length}/1000</p>

      <div className="flex gap-2 mt-3">
        <Button variant="ghost" size="md" onClick={() => setIsOpen(false)} className="flex-1">취소</Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSubmit}
          loading={createReview.isPending}
          disabled={rating === 0 || content.trim().length < 10}
          className="flex-1"
        >
          등록
        </Button>
      </div>
    </Card>
  )
}

/** 답변 접기/펼치기 기준 글자 수 */
const REPLY_PREVIEW_LENGTH = 100

/** 후기 카드 */
function ReviewCard({ review, facilityId }: { review: any; facilityId: number }) {
  const deleteReview = useDeleteReview()
  const [replyExpanded, setReplyExpanded] = useState(false)

  const replyIsLong = review.replyContent && review.replyContent.length > REPLY_PREVIEW_LENGTH

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={14} className={n <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-cb-gray-200'} />
            ))}
          </div>
          <span className="text-caption font-medium text-cb-gray-700">{review.userName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-caption text-cb-gray-400">
            {new Date(review.createdAt).toLocaleDateString('ko-KR')}
          </span>
          {review.isMine && (
            <button
              onClick={() => deleteReview.mutate({ reviewId: review.id, facilityId })}
              className="p-1 rounded-full hover:bg-cb-gray-100 text-cb-gray-400 hover:text-cb-danger"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      <p className="text-body-sm text-cb-gray-700 leading-relaxed whitespace-pre-line">{review.content}</p>

      {/* 시설 답변 (Step 6-A) — 토글형 (긴 답변은 접기/펼치기) */}
      {review.replyContent && (
        <div className="mt-3 rounded-xl bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare size={14} className="text-blue-600" />
            <span className="text-caption font-semibold text-blue-700">
              🏥 {review.replyByName ?? '시설'} 답변
            </span>
            {review.repliedAt && (
              <span className="text-caption text-blue-400">
                {new Date(review.repliedAt).toLocaleDateString('ko-KR')}
              </span>
            )}
          </div>
          <p className="text-body-sm text-blue-900 leading-relaxed whitespace-pre-line">
            {replyIsLong && !replyExpanded
              ? review.replyContent.slice(0, REPLY_PREVIEW_LENGTH) + '...'
              : review.replyContent}
          </p>
          {replyIsLong && (
            <button
              type="button"
              onClick={() => setReplyExpanded(!replyExpanded)}
              className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              {replyExpanded ? '접기' : '더보기'}
            </button>
          )}
        </div>
      )}
    </Card>
  )
}
